import { Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

interface TimerProps {
  startTime: dayjs.Dayjs | null;
  completedTime: dayjs.Dayjs | null;
  currentStep?: string;
  stepSequence?: string[];
  orderId?: number;
}

export const Timer = ({
  startTime,
  completedTime,
  currentStep,
  stepSequence,
  orderId,
}: TimerProps) => {
  const [seconds, setSeconds] = useState<number>(0);
  const [displayTime, setDisplayTime] = useState<number>(0);
  const [stepDurations, setStepDurations] = useState<Record<string, number>>(
    {},
  );

  const getTime = () => {
    // Get difference between current time and start time
    const current = completedTime ? completedTime : dayjs();
    const difference = current.diff(startTime, "second");
    setSeconds(difference);

    // Update display time here to avoid infinite re-renders
    updateDisplayTime(difference);
  };

  const updateDisplayTime = (rawSeconds: number) => {
    if (
      !currentStep ||
      !stepSequence ||
      Object.keys(stepDurations).length === 0
    ) {
      return;
    }

    const currentStepIndex = stepSequence.indexOf(currentStep);

    // If completed, calculate final total from all step durations
    if (completedTime && completedTime.isValid()) {
      const finalTime = Object.values(stepDurations).reduce(
        (sum, duration) => sum + duration,
        0,
      );
      setDisplayTime((prev) => Math.max(prev, finalTime));
      return;
    }

    // Calculate target display time based on step progression
    let targetDisplayTime = 0;

    // Add completed steps (use their random durations)
    for (let i = 0; i < currentStepIndex; i++) {
      const stepName = stepSequence[i];
      targetDisplayTime += stepDurations[stepName] || 120; // Fallback to 2 minutes
    }

    // Calculate progress within current step
    const timeInCurrentStep = rawSeconds % 15; // Each step is 15 seconds
    const progressInCurrentStep = Math.min(timeInCurrentStep / 15, 1);

    if (currentStep === "Plan completion") {
      // During plan completion: add the full optimisation duration (20 mins) plus completion progress
      const completionDuration = stepDurations["Plan completion"] || 120;
      targetDisplayTime += completionDuration; // Add full completion duration instantly
    } else {
      // All other steps: add proportional progress based on their random duration
      const currentStepDuration = stepDurations[currentStep] || 120;
      targetDisplayTime += progressInCurrentStep * currentStepDuration;
    }

    // Ensure monotonic progression - never go backwards
    setDisplayTime((prev) => Math.max(prev, targetDisplayTime));
  };

  const getCustomElapsedTime = () => {
    // Return the current display time - updates happen in updateDisplayTime()
    // Each step now has randomized durations (1-3 minutes) except optimisation (20 minutes)
    return displayTime;
  };

  const formatTime = () => {
    const customSeconds = getCustomElapsedTime();

    const d = Math.floor(customSeconds / (3600 * 24));
    const h = Math.floor((customSeconds % (3600 * 24)) / 3600);
    const m = Math.floor((customSeconds % 3600) / 60);
    const s = Math.floor(customSeconds % 60);

    const dDisplay = d > 0 ? d + (d == 1 ? " day " : " days ") : "";
    const hDisplay = h > 0 ? h + (h == 1 ? " hr " : " hrs ") : "";
    const mDisplay = m > 0 ? m + (m == 1 ? " min " : " mins ") : "";
    const sDisplay = s > 0 ? s + (s == 1 ? " sec" : " secs") : "";

    return dDisplay + hDisplay + mDisplay + sDisplay;
  };

  useEffect(() => {
    getTime();
    const interval = setInterval(() => getTime(), 1000);
    return () => clearInterval(interval);
  }, [startTime, completedTime]);

  // Initialize step durations when stepSequence or orderId changes
  useEffect(() => {
    if (!stepSequence) return;

    const durations: Record<string, number> = {};
    stepSequence.forEach((step, index) => {
      if (step === "Plan optimisation") {
        durations[step] = 20 * 60; // Keep optimisation at 20 minutes
      } else {
        // Seeded random 1-3 minutes for other steps (unique per order)
        const seed =
          (step.length + index) * (orderId || 1) + step.charCodeAt(0);
        const randomValue = ((seed * 9301 + 49297) % 233280) / 233280;
        durations[step] = (Math.floor(randomValue * 3) + 1) * 60; // 60, 120, or 180 seconds
      }
    });
    setStepDurations(durations);
  }, [stepSequence, orderId]);

  // Handle step changes to ensure proper display time updates
  useEffect(() => {
    if (seconds > 0) {
      updateDisplayTime(seconds);
    }
  }, [currentStep, stepSequence, stepDurations]);

  return (
    <Typography variant="body2" color="text.secondary">
      {startTime ? formatTime() : "-"}
    </Typography>
  );
};
